'use client';

import * as React from 'react';
import { Briefcase, Code, Shield } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TYPOGRAPHY, BORDERS } from '@/lib/design-tokens';

/**
 * RoleBasedCTA - Single role-specific call-to-action card
 *
 * Displays a single CTA card for a specific audience persona (Executive/Developer/Security).
 * Each instance renders only one card. Multiple instances can be placed in content to show
 * different roles side-by-side or sequentially.
 *
 * Features:
 * - Single card per instance (one role per component)
 * - Card-based design with hover effects
 * - Role-specific icons and color theming
 * - Analytics tracking for CTA clicks
 * - WCAG AA accessible
 * - Design token compliant
 *
 * Usage: Place multiple instances for different roles
 * Each instance shows only one card based on the role prop
 */

type RoleType = 'executive' | 'developer' | 'security';

interface RoleBasedCTAProps {
  /** Which role this card represents. Falls back to "developer" styling for unknown roles. */
  role: RoleType | string;
  /** Card title */
  title: string;
  /** Card description */
  description: string;
  /** Button text */
  buttonText: string;
  /** Button href (usually /contact?role=...) */
  buttonHref: string;
  /** Optional className */
  className?: string;
}

const roleConfig = {
  executive: { icon: Briefcase, label: 'For executives' },
  developer: { icon: Code, label: 'For developers' },
  security: { icon: Shield, label: 'For security teams' },
};

export function RoleBasedCTA({
  role,
  title,
  description,
  buttonText,
  buttonHref,
  className,
}: RoleBasedCTAProps) {
  const theme = roleConfig[role as RoleType] ?? roleConfig.developer;
  const Icon = theme.icon;

  const handleClick = () => {
    // Track CTA click event
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'cta_click', {
        cta_type: 'role_based',
        role,
        button_text: buttonText,
      });
    }
  };

  return (
    <div
      className={cn(
        'flex flex-col md:flex-row md:items-center gap-4 md:gap-6 p-6 border transition-colors my-8 w-full',
        BORDERS.card,
        'border-border/50 bg-muted/20 hover:border-border',
        'border-l-2 border-l-accent-700 dark:border-l-accent-400',
        className
      )}
      data-role={role}
      data-testid={`role-based-cta-${role}`}
    >
      {/* Content */}
      <div className="flex-1 min-w-0">
        <p
          className={cn(
            'flex items-center gap-2 mb-2 font-mono uppercase tracking-wider',
            TYPOGRAPHY.label.xs,
            'text-accent-700 dark:text-accent-400'
          )}
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
          {theme.label}
        </p>
        <h3 className={cn(TYPOGRAPHY.h3.standard, 'mb-2')}>{title}</h3>
        <p className={cn('text-sm leading-relaxed text-muted-foreground')}>{description}</p>
      </div>

      {/* Button */}
      <div className="shrink-0 w-full md:w-auto">
        <a
          href={buttonHref}
          onClick={handleClick}
          className={cn(
            'inline-flex items-center justify-center px-4 py-3 rounded-md whitespace-nowrap w-full md:w-auto',
            'text-sm font-medium transition-colors no-underline',
            'bg-primary text-primary-foreground hover:bg-primary/90',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'
          )}
          role="button"
          aria-label={`${buttonText} for ${title}`}
        >
          {buttonText}
        </a>
      </div>
    </div>
  );
}

export type { RoleBasedCTAProps };
